import { useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Custom hook to send heartbeat signals for real-time presence tracking.
 * Sends a heartbeat every 10 seconds when user is logged in for high-accuracy tracking.
 */
export const useHeartbeat = () => {
  const intervalRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const sendHeartbeat = async () => {
      try {
        await axios.post(
          `${API}/user/heartbeat`,
          {},
          { 
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          }
        );
      } catch (err) {
        console.debug('Heartbeat failed:', err.message);
      }
    };

    // Send initial heartbeat immediately
    sendHeartbeat();

    // High-frequency heartbeat every 10 seconds for real-time accuracy
    intervalRef.current = setInterval(sendHeartbeat, 10000);

    // Also send heartbeat on user activity (throttled to 15s)
    let lastActivityHeartbeat = Date.now();
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityHeartbeat > 15000) {
        lastActivityHeartbeat = now;
        sendHeartbeat();
      }
    };

    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('mousemove', handleActivity);

    // Send offline signal when tab closes
    const handleBeforeUnload = () => {
      const blob = new Blob(
        [JSON.stringify({})],
        { type: 'application/json' }
      );
      navigator.sendBeacon(`${API}/user/heartbeat-offline`, blob);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
};

export default useHeartbeat;
